/**
 * Careers quote slider — testimonial carousel with prev/next and counter.
 */
class DevCareersQuote extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number} */
  #activeIndex = 0;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#activeIndex = this.#getInitialIndex();
    this.#updateSlides();

    this.querySelector('[data-ref="prev"]')?.addEventListener('click', this.#onPrev, { signal });
    this.querySelector('[data-ref="next"]')?.addEventListener('click', this.#onNext, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /** @returns {number} */
  #getInitialIndex() {
    const [quoteSlides] = this.#getSlideGroups();
    const openIndex = quoteSlides.findIndex((slide) => !slide.hidden);
    return openIndex >= 0 ? openIndex : 0;
  }

  /** @returns {HTMLElement[][]} */
  #getSlideGroups() {
    const quoteSlides = Array.from(this.querySelectorAll('[data-careers-slide-type="quote"]'));
    const mediaSlides = Array.from(this.querySelectorAll('[data-careers-slide-type="media"]'));
    return [quoteSlides, mediaSlides];
  }

  #updateSlides() {
    const [quoteSlides, mediaSlides] = this.#getSlideGroups();
    const total = quoteSlides.length;

    const toggleGroup = (slides) => {
      slides.forEach((slide, index) => {
        const isActive = index === this.#activeIndex;
        slide.hidden = !isActive;
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });
    };

    toggleGroup(quoteSlides);
    toggleGroup(mediaSlides);

    const counter = this.querySelector('[data-ref="counter"]');
    if (counter && total > 0) {
      counter.textContent = `${this.#activeIndex + 1}/${total}`;
    }

    const prev = this.querySelector('[data-ref="prev"]');
    const next = this.querySelector('[data-ref="next"]');

    if (prev instanceof HTMLButtonElement) {
      prev.disabled = total <= 1;
    }

    if (next instanceof HTMLButtonElement) {
      next.disabled = total <= 1;
    }
  }

  /** @param {number} direction */
  #go(direction) {
    const [quoteSlides] = this.#getSlideGroups();
    if (quoteSlides.length <= 1) return;

    this.#activeIndex = (this.#activeIndex + direction + quoteSlides.length) % quoteSlides.length;
    this.#updateSlides();
  }

  #onPrev = () => {
    this.#go(-1);
  };

  #onNext = () => {
    this.#go(1);
  };
}

if (!customElements.get('dev-careers-quote')) {
  customElements.define('dev-careers-quote', DevCareersQuote);
}
